import {
  EscHotKeyIcon,
  ReturnHotKeyIcon,
} from '@modules/common/components/HotKeyIcons';
import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import ModalHeader from '@modules/common/components/Modal/ModalHeader';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { TRACKED_KEYWORD } from '@modules/common/constants';
import { Category } from '@modules/common/types';
import { AddNewTrackedKeywordForm } from '@modules/feedback/components/AddNewTrackedKeyword/AddNewTrackedKeywordForm';
import { AddNewTrackedKeywordIllustration } from '@modules/feedback/components/AddNewTrackedKeyword/AddNewTrackedKeywordIllustration';
import { UpdateTrackedKeywordCategory } from '@modules/feedback/components/AddNewTrackedKeyword/UpdateTrackedKeywordCategory';
import { TRACKED_KEYWORD_ALREADY_EXISTS_ERROR } from '@modules/taxonomy/constants';
import { trackTaxonomyEvent } from '@modules/taxonomy/helpers/trackTaxonomyEvents';
import { useCreateTrackedKeyword } from '@modules/taxonomy/hooks';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  Divider,
  Grid,
} from '@mui/material';
import { FormEvent, useCallback, useState } from 'react';
import { UUID } from 'v2/core/entities/uuid';
import ErrorCode from 'v2/core/errors/codes';
import { isErrorCode } from 'v2/core/errors/errors';

interface AddNewTrackedKeywordProps {
  feedbackID: UUID;
  name: string;
  onCreateNewTrackedKeyword: () => void;
  isDetailView: boolean;
}

export const AddNewTrackedKeyword = ({
  feedbackID,
  name,
  onCreateNewTrackedKeyword,
  isDetailView,
}: AddNewTrackedKeywordProps) => {
  const { closeModal } = useMuiModalContext();
  const { showErrorNotification } = useNotificationContext();

  const [phraseList, setPhraseList] = useState<string[]>([]);
  const [trackedKeywordName, setTrackedKeywordName] = useState<string>(name);
  const [selectedCategory, setSelectedCategory] = useState<
    Category | undefined
  >();
  const [labelID, setLabelID] = useState('');

  const { createTrackedKeyword, loading } = useCreateTrackedKeyword({
    createKeywordInput: {
      displayName: trackedKeywordName,
      phrases: phraseList,
    },
    onCompleted: (data) => {
      const createdTrackedKeywordID = data.createKeyword?.id;
      if (createdTrackedKeywordID) {
        setLabelID(createdTrackedKeywordID);
        trackTaxonomyEvent({
          eventName: 'Add New Tracked Keyword',
          data: {
            id: createdTrackedKeywordID,
            displayName: trackedKeywordName,
            phrases: phraseList,
          },
        });
      }
    },
    onError: (error) => {
      if (isErrorCode(error, ErrorCode.DuplicateRecord)) {
        showErrorNotification({
          contentComponent: TRACKED_KEYWORD_ALREADY_EXISTS_ERROR,
          props: {},
        });
        return;
      }
      showErrorNotification({
        contentComponent: `Failed to create the Tracked Keyword.`,
        props: {},
      });
    },
  });

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    createTrackedKeyword();
  };

  const onUpdateCategory = useCallback(() => {
    setLabelID('');
    closeModal();
    onCreateNewTrackedKeyword();
  }, [closeModal, onCreateNewTrackedKeyword]);

  const onCancel = () => closeModal();

  const isDisabled =
    !trackedKeywordName || !selectedCategory || !phraseList.length;

  return (
    <Box
      sx={{
        overflow: 'hidden',
      }}
      component="form"
      onSubmit={onSubmit}
    >
      <ModalHeader title={`Create new ${TRACKED_KEYWORD}`} />
      <Divider />
      <DialogContent
        sx={{
          height: '550px',
        }}
      >
        <Grid container maxWidth={1200}>
          <Grid item xs={5.5}>
            <AddNewTrackedKeywordForm
              feedbackID={feedbackID}
              phraseList={phraseList}
              setPhraseList={setPhraseList}
              trackedKeywordName={trackedKeywordName}
              setTrackedKeywordName={setTrackedKeywordName}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
            />
          </Grid>
          <Grid item xs={1} display="flex" justifyContent="center">
            <Divider
              sx={{
                height: '100% !important',
                width: '1px',
              }}
            />
          </Grid>
          <Grid item xs={5.5}>
            <AddNewTrackedKeywordIllustration />
          </Grid>
        </Grid>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ m: 1, mr: 2 }}>
        <Button
          variant="outlined"
          onClick={onCancel}
          type="button"
          endIcon={<EscHotKeyIcon />}
        >
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          type="submit"
          loading={loading}
          endIcon={<ReturnHotKeyIcon />}
          disabled={isDisabled}
        >
          Add
        </LoadingButton>
      </DialogActions>
      <UpdateTrackedKeywordCategory
        feedbackID={feedbackID}
        labelID={labelID}
        labelName={trackedKeywordName}
        category={selectedCategory}
        onUpdateCategory={onUpdateCategory}
        isDetailView={isDetailView}
      />
    </Box>
  );
};
